import { T } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { type ReactNode } from 'react';
import { Linking } from 'react-native';

import EmployeeWellness from '@/assets/images/onboarding/employee-wellness.svg';
import {
  ContactSupport,
  CtaButton,
  DEFAULT_EMAIL,
  Markdown,
  Screen,
  ScreenHeading,
} from '@/modules/ui';

type IneligibleTemplateProps = {
  /** Centered heading. Omitted when `markdown` is provided. */
  title?: string;
  /** Optional sub-heading under the title. */
  subtitle?: string;
  /** Extra body content under the heading (e.g. the not-qualified paragraphs). */
  body?: ReactNode;
  /** Org-configured fallback copy (markdown); replaces title/subtitle/body. */
  markdown?: string;
};

/**
 * Shared layout for the terminal not-eligible variants (terminated insurance +
 * not-qualified), mirroring stylos's `IneligiblePage`: the EmployeeWellness
 * hero, centered title/subtitle (or an org `fallbackMessage` markdown / custom
 * body), and a footer with ContactSupport + an Email Support button. It's a
 * dead end — no retry, unlike the records-not-found variant.
 *
 * The `markdown` (org fallbackMessage) variant renders NO footer: that copy
 * already embeds its own support contacts, so a second ContactSupport footer
 * would duplicate them (mirrors Sparta's `pageFooter={null}` for this case).
 */
export function IneligibleTemplate({ title, subtitle, body, markdown }: IneligibleTemplateProps) {
  return (
    <Screen
      contentStyle={{ justifyContent: 'center' }}
      footer={
        markdown ? undefined : (
          <VStack space='lg'>
            <ContactSupport showEmail showPhone className='text-center' />
            <CtaButton
              onPress={() => void Linking.openURL(`mailto:${DEFAULT_EMAIL}`).catch(() => {})}
              testID='not-eligible-email-support'
            >
              <T _str='Email Support' />
            </CtaButton>
          </VStack>
        )
      }
    >
      <VStack space='2xl' className='items-center'>
        <EmployeeWellness width={221} height={132} />
        {markdown ? (
          <Markdown>{markdown}</Markdown>
        ) : (
          <VStack space='xl' className='items-center'>
            {title ? <ScreenHeading title={title} subtitle={subtitle} center /> : null}
            {body}
          </VStack>
        )}
      </VStack>
    </Screen>
  );
}
